import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useScrollReveal } from "./useAnimations";
import "./FAQSection.scss";

const defaultFaqs = [
  {
    q: "What services does a software development company provide?",
    a:
      "A software development company offers a wide range of services, including custom software development, web and mobile app development, system integration, technology audits, cloud solutions, and ongoing support. These services help businesses build, optimise, and scale digital products efficiently.",
  },
  {
    q:
      "What is the difference between a software development company and a software development agency?",
    a:
      "While both deliver software solutions, a software development company typically focuses on long-term, end-to-end development and scalability. A software development agency may prioritise shorter-term projects, design, or marketing-driven solutions.",
  },
  {
    q: "What should businesses look for in an IT software development company?",
    a:
      "Businesses should look for technical expertise, proven experience, scalability-focused solutions, transparent processes, and strong communication.",
  },
  {
    q:
      "How do I choose the right software development company for my business?",
    a:
      "Start by evaluating their portfolio, industry experience, and client reviews. Assess their technical capabilities, development process, and ability to understand your business needs.",
  },
  {
    q: "What is included in custom software development services?",
    a:
      "Custom software development includes requirement analysis, UI/UX design, development, testing, deployment, and ongoing maintenance. It is tailored to meet specific business needs, ensuring better performance, flexibility, and scalability.",
  },
  {
    q:
      "What industries do custom software development companies typically serve?",
    a:
      "Custom software development companies work across industries such as healthcare, fintech, retail, logistics, education, and more. Solutions are tailored to meet the unique challenges and regulatory requirements of each sector.",
  },
  {
    q: "How long does custom software development usually take?",
    a:
      "Timelines vary depending on the complexity and scope of the project. Simple applications may take a few weeks, while more complex, enterprise-level solutions can take several months.",
  },
  {
    q: "Can a software development company help modernise legacy systems?",
    a:
      "Yes, modernising legacy systems is a key service. This involves upgrading outdated technologies, improving performance, enhancing security, and ensuring the system can scale with future business demands.",
  },
  {
    q: "What is an AI development company and what services does it provide?",
    a:
      "An AI development company specialises in building intelligent systems using technologies like machine learning, natural language processing, and predictive analytics. Services include AI model development, automation solutions, data analysis, and AI integration into existing systems.",
  },
  {
    q:
      "What are AI development services and how can they benefit my organisation?",
    a:
      "AI development services help automate processes, improve decision-making, enhance customer experiences, and uncover valuable insights from data. This leads to increased efficiency, reduced costs, and a competitive advantage.",
  },
  {
    q:
      "What is digital transformation consulting and why is it important for businesses?",
    a:
      "Digital transformation consulting helps businesses adopt modern technologies to improve operations, customer experience, and scalability. It is essential for staying competitive in a rapidly evolving digital landscape.",
  },
  {
    q: "What does a business transformation consultant do?",
    a:
      "A business transformation consultant analyses existing processes, identifies inefficiencies, and recommends strategic changes. This may include technology upgrades, process optimisation, and organisational improvements to drive growth.",
  },
  {
    q:
      "How can business transformation consulting improve operational efficiency?",
    a:
      "It improves efficiency by streamlining workflows, reducing manual processes, integrating systems, and leveraging data-driven insights. This results in faster operations, lower costs, and better resource utilisation.",
  },
  {
    q: "What are the benefits of outsourcing software development?",
    a:
      "Outsourcing provides access to skilled talent, reduces operational costs, speeds up development, and allows businesses to focus on core activities while experts handle the technical aspects.",
  },
  {
    q:
      "Is software outsourcing development suitable for startups and growing businesses?",
    a:
      "Yes, outsourcing is highly beneficial for startups and growing businesses as it offers flexibility, cost-efficiency, and access to specialised expertise without the need to build an in-house team.",
  },
  {
    q:
      "How do software development companies ensure scalable and secure applications?",
    a:
      "They use modern architectures, cloud infrastructure, rigorous testing, and security best practices. Regular performance monitoring and updates ensure applications remain scalable, reliable, and secure as the business grows.",
  },
];

const FAQItem = ({ faq, isOpen, onToggle, index }) => {
  const [ref, vis] = useScrollReveal({ threshold: 0.1 });

  return (
    <div
      ref={ref}
      className={`faq__item${isOpen ? " faq__item--open" : ""}${
        vis ? " is-visible" : ""
      }`}
      style={{ transitionDelay: `${index * 0.05}s` }}
    >
      <button className="faq__q" onClick={onToggle} aria-expanded={isOpen}>
        <span className="faq__q-text">{faq.q}</span>
        <span className={`faq__q-icon${isOpen ? " faq__q-icon--open" : ""}`}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="M7 1v12M1 7h12" />
          </svg>
        </span>
      </button>
      <div className={`faq__a-wrap${isOpen ? " faq__a-wrap--open" : ""}`}>
        <p className="faq__a">{faq.a}</p>
      </div>
    </div>
  );
};

const INITIAL_VISIBLE = 4;

const FAQSection = ({ faqs: faqsProp }) => {
  const faqs = faqsProp || defaultFaqs;
  const [openIdx, setOpenIdx] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [hRef, hVis] = useScrollReveal();

  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_VISIBLE);

  const faqPageJSONLD = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section className="faq">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageJSONLD)}
        </script>
      </Helmet>
      <div className="faq__inner">
        <div ref={hRef} className="faq__header">
          <span className={`faq__label${hVis ? " is-visible" : ""}`}>FAQ</span>
          <h2 className={`faq__title${hVis ? " is-visible" : ""}`}>
            Frequently Asked <em>Questions</em>
          </h2>
          <p className={`faq__subtitle${hVis ? " is-visible" : ""}`}>
            Everything you need to know about working with a software
            development company — answered clearly.
          </p>
        </div>

        <div className="faq__layout">
          {/* FAQ items */}
          <div className="faq__list">
            {visibleFaqs.map((f, i) => (
              <FAQItem
                key={i}
                faq={f}
                index={i}
                isOpen={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? null : i)}
              />
            ))}
          </div>
        </div>

        {!showAll && (
          <div className="faq__view-more">
            <button
              className="faq__view-more-btn"
              onClick={() => setShowAll(true)}
            >
              View More Questions
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M8 3v10M3 8l5 5 5-5" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;
